import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, Icon, CheckboxFilter } from "../../components/tailwind";
import { RootState } from "../../utils/store";
import { FilterOptions, getFilterOptions } from "../../api/openrouterApi";

export type ViewMode = "grid" | "list";

type Props = {
  search: string;
  providers: string[];
  supportedParameters: string[];
  modelTiers: string[];
  addedModels: string[];
  viewMode: ViewMode;
  onSearchChange: (search: string) => void;
  onProvidersChange: (providers: string[]) => void;
  onSupportedParametersChange: (params: string[]) => void;
  onModelTiersChange: (tiers: string[]) => void;
  onAddedModelsChange: (addedModels: string[]) => void;
  onClearFilters: () => void;
  onViewModeChange: (mode: ViewMode) => void;
};

export const ModelFilters: React.FC<Props> = ({
  search,
  providers,
  supportedParameters,
  modelTiers,
  addedModels,
  viewMode,
  onSearchChange,
  onProvidersChange,
  onSupportedParametersChange,
  onModelTiersChange,
  onAddedModelsChange,
  onClearFilters,
  onViewModeChange,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(
    null
  );
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [localSearch, setLocalSearch] = useState(search);

  // Sync local search state when search prop changes (e.g., when cleared)
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    const fetchOptions = async (): Promise<void> => {
      if (!token) return;
      try {
        setLoadingOptions(true);
        const options = await getFilterOptions(token);
        setFilterOptions(options);
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, [token]);

  const hasActiveFilters =
    search.trim() !== "" ||
    providers.length > 0 ||
    supportedParameters.length > 0 ||
    modelTiers.length > 0 ||
    addedModels.length > 0;

  // Convert string arrays to { value, label } format for CheckboxFilter
  const providerOptions = useMemo(
    () => (filterOptions?.providers || []).map((p) => ({ value: p, label: p })),
    [filterOptions?.providers]
  );

  const parameterOptions = useMemo(
    () =>
      (filterOptions?.supported_parameters || []).map((p) => ({
        value: p,
        label: p,
      })),
    [filterOptions?.supported_parameters]
  );

  const addedOptions = useMemo(
    () =>
      (filterOptions?.addedModels || []).map((a) => ({
        value: a,
        label: a,
      })),
    [filterOptions?.addedModels]
  );

  const tierOptions = useMemo(
    () =>
      (filterOptions?.tiers || []).map(([name, displayValue]) => ({
        value: name,
        label: displayValue,
      })),
    [filterOptions?.tiers]
  );

  return (
    <div className="tw-mb-6 tw-space-y-4">
      {/* Search Bar - Own Row */}
      <div>
        <form
          onSubmit={(e): void => {
            e.preventDefault();
            onSearchChange(localSearch);
          }}
          className="tw-flex tw-gap-2 tw-max-w-md"
        >
          <input
            name="search"
            type="text"
            value={localSearch}
            onChange={(e): void => setLocalSearch(e.target.value)}
            placeholder="Search by model name..."
            className="tw-flex-1 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-border-transparent"
          />
          <Button type="submit" variant="primary" size="sm" icon>
            <Icon name="search" />
            Search
          </Button>
        </form>
      </div>

      {/* Filters row: grid of dropdowns + clear button, toggle pinned right */}
      <div className="tw-flex tw-items-end tw-gap-4">
        <div className="tw-flex-1 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-items-end">
          {/* Provider Filter */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Provider
            </label>
            <CheckboxFilter
              options={providerOptions}
              selected={providers}
              onChange={onProvidersChange}
              placeholder="Select providers..."
              disabled={loadingOptions}
              testIdBase="provider-filter"
            />
          </div>

          {/* Supported Parameters Filter */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Supported Parameters
            </label>
            <CheckboxFilter
              options={parameterOptions}
              selected={supportedParameters}
              onChange={onSupportedParametersChange}
              placeholder="Select parameters..."
              disabled={loadingOptions}
              testIdBase="parameter-filter"
            />
          </div>

          {/* Model Tiers Filter */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Model Tiers
            </label>
            <CheckboxFilter
              options={tierOptions}
              selected={modelTiers}
              onChange={onModelTiersChange}
              placeholder="Select model tiers..."
              disabled={loadingOptions}
              testIdBase="tier-filter"
            />
          </div>

          {/* Added Models Filter */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Added Models
            </label>
            <CheckboxFilter
              options={addedOptions}
              selected={addedModels}
              onChange={onAddedModelsChange}
              placeholder="Select type..."
              disabled={loadingOptions}
              testIdBase="added-models-filter"
            />
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div>
              <Button
                variant="secondary-alt"
                size="sm"
                icon
                onClick={onClearFilters}
              >
                <Icon name="delete" />
                Clear filters
              </Button>
            </div>
          )}
        </div>

        {/* View Mode Toggle */}
        <div className="tw-flex tw-gap-1 tw-shrink-0">
          <Button
            variant={viewMode === "grid" ? "primary" : "secondary-alt"}
            size="sm"
            icon
            compact
            onClick={(): void => onViewModeChange("grid")}
            title="Grid view"
          >
            <Icon name="view_module" />
          </Button>
          <Button
            variant={viewMode === "list" ? "primary" : "secondary-alt"}
            size="sm"
            icon
            compact
            onClick={(): void => onViewModeChange("list")}
            title="List view"
          >
            <Icon name="view_list" />
          </Button>
        </div>
      </div>
    </div>
  );
};
